import { NgClass, NgFor, NgIf, UpperCasePipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { StatusCategoryBadgeComponent } from '../../../../../shared/components/status-category-badge/status-category-badge.component';
import { EWorkflow, EWorkflowIcon } from '../../../../items/items.interface';
import { IWorkflowStepOption, IWorkflowTransition } from '../workflows.interface';

export interface IWorkflowDiagramData {
  name: string;
  startWorkflowStepId: number | null;
  steps: IWorkflowStepOption[];
  transitions: IWorkflowTransition[];
}

interface IDiagramNode {
  category: EWorkflow;
  dim: boolean;
  icon: string;
  id: number;
  isStart: boolean;
  label: string;
  x: number;
  y: number;
}

const CATEGORY_ICON: Record<EWorkflow, EWorkflowIcon> = {
  [EWorkflow.todo]: EWorkflowIcon.adjust,
  [EWorkflow.inProgress]: EWorkflowIcon.schedule,
  [EWorkflow.done]: EWorkflowIcon.task_alt,
};

interface IDiagramEdge {
  active: boolean;
  category: EWorkflow;
  dim: boolean;
  path: string;
  sourceId: number;
  targetId: number;
}

const START_NODE_ID = -1;

const BOX_W = 200;
const BOX_H = 44;
const ROW_GAP = 104;
const TOP_PAD = 104;
const CENTER_X = 300;
const LANE_STEP = 24;
const LANE_BASE = 30;
const ATTACH_STEP = 11;
const CORNER = 8;
const START_Y = 34;

interface IEdgePlan {
  side: 1 | -1;
  sourceId: number;
  span: number;
  targetId: number;
}

interface IAttachment {
  edgeIndex: number;
  otherY: number;
  role: 'source' | 'target';
}

const CATEGORY_RANK: Record<EWorkflow, number> = {
  [EWorkflow.todo]: 0,
  [EWorkflow.inProgress]: 1,
  [EWorkflow.done]: 2,
};

@Component({
  imports: [
    FormsModule,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatIcon,
    MatIconButton,
    MatSlider,
    MatSliderThumb,
    NgClass,
    NgFor,
    NgIf,
    StatusCategoryBadgeComponent,
    TranslateModule,
    UpperCasePipe,
  ],
  selector: 'app-workflow-diagram-dialog',
  standalone: true,
  styleUrl: './workflow-diagram-dialog.component.scss',
  templateUrl: './workflow-diagram-dialog.component.html',
})
export class WorkflowDiagramDialogComponent {
  public readonly boxWidth = BOX_W;
  public readonly boxHeight = BOX_H;
  public readonly centerX = CENTER_X;
  public readonly startY = START_Y;
  public readonly arrowCategories: EWorkflow[] = Object.values(EWorkflow);
  public readonly legendItems: { category: EWorkflow; labelKey: string }[] = [
    { category: EWorkflow.todo, labelKey: 'system.label.todo' },
    { category: EWorkflow.inProgress, labelKey: 'system.label.inProgress' },
    { category: EWorkflow.done, labelKey: 'system.label.done' },
  ];

  public hoveredNodeId: number | null = null;
  public startDim = false;
  private readonly adjacency = new Map<number, Set<number>>();

  // Zoom & pan (JIRA-like map navigation)
  public zoom = 1;
  public panX = 0;
  public panY = 0;
  public isPanning = false;
  private panStart = { x: 0, y: 0 };
  public stageTransform = 'translate(0px, 0px) scale(1)';

  public nodes: IDiagramNode[] = [];
  public edges: IDiagramEdge[] = [];
  public startEdge: IDiagramEdge | null = null;
  public viewBox = '0 0 600 400';
  public svgWidth = 600;
  public svgHeight = 400;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: IWorkflowDiagramData,
    private readonly translate: TranslateService,
  ) {
    this.buildDiagram();
  }

  private buildDiagram(): void {
    const usedIds = new Set<number>();

    for (const transition of this.data.transitions) {
      usedIds.add(transition.workflowStepId);
      usedIds.add(transition.nextWorkflowStepId);
    }

    if (this.data.startWorkflowStepId) {
      usedIds.add(this.data.startWorkflowStepId);
    }

    const filteredSteps: IWorkflowStepOption[] = this.data.steps.filter((step: IWorkflowStepOption) =>
      usedIds.has(step.id),
    );

    // Lay steps out following the workflow flow: start status first, then downstream via transitions.
    const usedSteps: IWorkflowStepOption[] = this.orderStepsByFlow(filteredSteps);

    this.nodes = usedSteps.map(
      (step: IWorkflowStepOption, index: number): IDiagramNode => ({
        category: step.statusCategory,
        dim: false,
        icon: CATEGORY_ICON[step.statusCategory] ?? EWorkflowIcon.adjust,
        id: step.id,
        isStart: step.id === this.data.startWorkflowStepId,
        label: step.isDefault ? this.translate.instant(`system.label.${step.name}`) : step.name,
        x: CENTER_X,
        y: TOP_PAD + index * ROW_GAP,
      }),
    );

    const nodeById = new Map<number, IDiagramNode>(this.nodes.map((node: IDiagramNode) => [node.id, node]));

    // 1. Resolve edges and pick a side: downward transitions route on the right, upward on the left.
    const plans: IEdgePlan[] = this.data.transitions
      .map((transition: IWorkflowTransition): IEdgePlan | null => {
        const source: IDiagramNode | undefined = nodeById.get(transition.workflowStepId);
        const target: IDiagramNode | undefined = nodeById.get(transition.nextWorkflowStepId);

        if (!source || !target) {
          return null;
        }

        return {
          side: target.y >= source.y ? 1 : -1,
          sourceId: source.id,
          span: Math.abs(target.y - source.y),
          targetId: target.id,
        };
      })
      .filter((plan: IEdgePlan | null): plan is IEdgePlan => plan !== null);

    // 2. One dedicated vertical corridor (lane) per edge on each side; shorter edges stay closer to the boxes.
    const laneByEdge = new Map<number, number>();

    for (const side of [1, -1] as const) {
      plans
        .map((plan: IEdgePlan, index: number) => ({ index, plan }))
        .filter((entry) => entry.plan.side === side)
        .sort((first, second) => first.plan.span - second.plan.span)
        .forEach((entry, lane: number) => laneByEdge.set(entry.index, lane));
    }

    // 3. Spread the attachment points along each node's side so stubs never stack on the same spot.
    const attachmentsByNodeSide = new Map<string, IAttachment[]>();
    const attachKey = (nodeId: number, side: number): string => `${nodeId}:${side}`;
    const pushAttachment = (nodeId: number, side: number, attachment: IAttachment): void => {
      const key: string = attachKey(nodeId, side);
      attachmentsByNodeSide.set(key, [...(attachmentsByNodeSide.get(key) ?? []), attachment]);
    };

    plans.forEach((plan: IEdgePlan, index: number) => {
      pushAttachment(plan.sourceId, plan.side, {
        edgeIndex: index,
        otherY: nodeById.get(plan.targetId)!.y,
        role: 'source',
      });
      pushAttachment(plan.targetId, plan.side, {
        edgeIndex: index,
        otherY: nodeById.get(plan.sourceId)!.y,
        role: 'target',
      });
    });

    const attachYByEdge = new Map<string, number>();

    for (const [key, attachments] of attachmentsByNodeSide) {
      const nodeId = Number(key.split(':')[0]);
      const nodeY: number = nodeById.get(nodeId)!.y;
      const usableHeight: number = Math.min(BOX_H - 12, (attachments.length - 1) * ATTACH_STEP);
      const startY: number = nodeY - usableHeight / 2;

      attachments
        .sort((first: IAttachment, second: IAttachment) => first.otherY - second.otherY)
        .forEach((attachment: IAttachment, rank: number) => {
          const step: number = attachments.length > 1 ? usableHeight / (attachments.length - 1) : 0;
          attachYByEdge.set(`${attachment.edgeIndex}:${attachment.role}`, startY + rank * step);
        });
    }

    // 4. Build orthogonal (elbow) paths: out to the lane, along it, then into the target.
    this.edges = plans.map((plan: IEdgePlan, index: number): IDiagramEdge => {
      const boxEdgeX: number = CENTER_X + plan.side * (BOX_W / 2);
      const laneX: number = boxEdgeX + plan.side * (LANE_BASE + (laneByEdge.get(index) ?? 0) * LANE_STEP);
      const sy: number = attachYByEdge.get(`${index}:source`) ?? nodeById.get(plan.sourceId)!.y;
      const ty: number = attachYByEdge.get(`${index}:target`) ?? nodeById.get(plan.targetId)!.y;

      return {
        active: false,
        category: nodeById.get(plan.targetId)!.category,
        dim: false,
        path: this.buildElbowPath(boxEdgeX, sy, laneX, ty),
        sourceId: plan.sourceId,
        targetId: plan.targetId,
      };
    });

    // adjacency for hover emphasis (which nodes are directly connected)
    for (const plan of plans) {
      this.addAdjacency(plan.sourceId, plan.targetId);
      this.addAdjacency(plan.targetId, plan.sourceId);
    }

    const startNode: IDiagramNode | undefined = this.nodes.find((node: IDiagramNode) => node.isStart);

    this.startEdge = startNode
      ? {
          active: false,
          category: startNode.category,
          dim: false,
          path: `M ${CENTER_X} ${START_Y + 10} L ${CENTER_X} ${startNode.y - BOX_H / 2}`,
          sourceId: START_NODE_ID,
          targetId: startNode.id,
        }
      : null;

    const rightCount: number = plans.filter((plan: IEdgePlan) => plan.side === 1).length;
    const leftCount: number = plans.filter((plan: IEdgePlan) => plan.side === -1).length;
    const maxRightOffset: number = LANE_BASE + Math.max(rightCount - 1, 0) * LANE_STEP + 24;
    const maxLeftOffset: number = LANE_BASE + Math.max(leftCount - 1, 0) * LANE_STEP + 24;
    const minX: number = CENTER_X - BOX_W / 2 - maxLeftOffset;
    const maxX: number = CENTER_X + BOX_W / 2 + maxRightOffset;

    this.svgWidth = maxX - minX;
    this.svgHeight = TOP_PAD + Math.max(this.nodes.length, 1) * ROW_GAP;
    this.viewBox = `${minX} 0 ${this.svgWidth} ${this.svgHeight}`;
  }

  // Order steps by the workflow flow (breadth-first from the start status) so the diagram reads top-down.
  // Steps unreachable from the start fall back to the previous category ordering for a stable layout.
  private orderStepsByFlow(steps: IWorkflowStepOption[]): IWorkflowStepOption[] {
    const stepById = new Map<number, IWorkflowStepOption>(
      steps.map((step: IWorkflowStepOption) => [step.id, step]),
    );

    const forward = new Map<number, number[]>();

    for (const transition of this.data.transitions) {
      if (!stepById.has(transition.workflowStepId) || !stepById.has(transition.nextWorkflowStepId)) {
        continue;
      }

      const targets: number[] = forward.get(transition.workflowStepId) ?? [];
      targets.push(transition.nextWorkflowStepId);
      forward.set(transition.workflowStepId, targets);
    }

    const ordered: IWorkflowStepOption[] = [];
    const visited = new Set<number>();
    const startId: number | null = this.data.startWorkflowStepId;
    const queue: number[] = startId && stepById.has(startId) ? [startId] : [];

    while (queue.length) {
      const currentId: number = queue.shift()!;

      if (visited.has(currentId)) {
        continue;
      }

      visited.add(currentId);
      ordered.push(stepById.get(currentId)!);

      for (const nextId of forward.get(currentId) ?? []) {
        if (!visited.has(nextId)) {
          queue.push(nextId);
        }
      }
    }

    const leftovers: IWorkflowStepOption[] = steps
      .filter((step: IWorkflowStepOption) => !visited.has(step.id))
      .sort(
        (first: IWorkflowStepOption, second: IWorkflowStepOption) =>
          CATEGORY_RANK[first.statusCategory] - CATEGORY_RANK[second.statusCategory] ||
          first.name.localeCompare(second.name),
      );

    return [...ordered, ...leftovers];
  }

  private updateStageTransform(): void {
    this.stageTransform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
  }

  public zoomIn(): void {
    this.zoom = Math.min(2, Math.round((this.zoom + 0.1) * 10) / 10);
    this.updateStageTransform();
  }

  public zoomOut(): void {
    this.zoom = Math.max(0.4, Math.round((this.zoom - 0.1) * 10) / 10);
    this.updateStageTransform();
  }

  public setZoom(zoom: number): void {
    this.zoom = zoom;
    this.updateStageTransform();
  }

  public onPanStart(event: PointerEvent): void {
    this.isPanning = true;
    this.panStart = { x: event.clientX - this.panX, y: event.clientY - this.panY };
  }

  public onPanMove(event: PointerEvent): void {
    if (!this.isPanning) {
      return;
    }

    this.panX = event.clientX - this.panStart.x;
    this.panY = event.clientY - this.panStart.y;
    this.updateStageTransform();
  }

  public onPanEnd(): void {
    this.isPanning = false;
  }

  // Recompute node/edge emphasis once per hover change instead of calling predicates from the template every cycle.
  public setHovered(nodeId: number | null): void {
    this.hoveredNodeId = nodeId;

    const hovered: number | null = this.hoveredNodeId;
    const neighbours: Set<number> | undefined = hovered === null ? undefined : this.adjacency.get(hovered);

    for (const node of this.nodes) {
      node.dim = hovered !== null && node.id !== hovered && !neighbours?.has(node.id);
    }

    for (const edge of this.edges) {
      edge.active = hovered !== null && (edge.sourceId === hovered || edge.targetId === hovered);
      edge.dim = hovered !== null && !edge.active;
    }

    if (this.startEdge) {
      this.startEdge.active =
        hovered !== null && (this.startEdge.sourceId === hovered || this.startEdge.targetId === hovered);
      this.startEdge.dim = hovered !== null && !this.startEdge.active;
    }

    this.startDim = hovered !== null && hovered !== this.data.startWorkflowStepId;
  }

  private addAdjacency(from: number, to: number): void {
    const neighbours: Set<number> = this.adjacency.get(from) ?? new Set<number>();
    neighbours.add(to);
    this.adjacency.set(from, neighbours);
  }

  private buildElbowPath(boxEdgeX: number, sy: number, laneX: number, ty: number): string {
    const towardLane: number = Math.sign(laneX - boxEdgeX) || 1;
    const towardVertical: number = Math.sign(ty - sy) || 1;
    const corner: number = CORNER;

    // horizontal out, rounded corner, vertical along lane, rounded corner, horizontal into target
    return [
      `M ${boxEdgeX} ${sy}`,
      `L ${laneX - towardLane * corner} ${sy}`,
      `Q ${laneX} ${sy} ${laneX} ${sy + towardVertical * corner}`,
      `L ${laneX} ${ty - towardVertical * corner}`,
      `Q ${laneX} ${ty} ${laneX - towardLane * corner} ${ty}`,
      `L ${boxEdgeX} ${ty}`,
    ].join(' ');
  }
}
